import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import AppHeader from "./components/AppHeader";
import { apiPost, apiUrl, fetchJson } from "./api";

const TOKEN_SCOPE_NOTE =
  "This token is used only to read module items (pages, files, links) from Canvas so we can extract instructional content for DeepToast.";

function Modal({ open, title, onClose, children }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" role="dialog" aria-modal="true">
      <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[85vh] overflow-y-auto p-6 relative">
        <button
          type="button"
          className="absolute top-4 right-4 text-slate-500 hover:text-slate-800 text-xl leading-none"
          onClick={onClose}
          aria-label="Close"
        >
          ×
        </button>
        <h3 className="text-xl font-bold text-primary mb-4 pr-8">{title}</h3>
        <div className="text-slate-700 space-y-3 text-sm leading-relaxed">{children}</div>
        <div className="mt-6 flex justify-end">
          <button
            type="button"
            onClick={onClose}
            className="bg-primary text-white px-4 py-2 rounded-md font-semibold hover:opacity-95"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default function OnboardingPage({ session }) {
  const navigate = useNavigate();
  const userId = session?.user?.id;

  const [canvasBaseUrl, setCanvasBaseUrl] = useState("");
  const [apiToken, setApiToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");
  const [hasToken, setHasToken] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);

  const [courses, setCourses] = useState([]);
  const [coursesLoading, setCoursesLoading] = useState(false);
  const [selectedCourseId, setSelectedCourseId] = useState("");

  const [modules, setModules] = useState([]);
  const [modulesLoading, setModulesLoading] = useState(false);
  const [selectedModuleIds, setSelectedModuleIds] = useState(() => new Set());

  const [extractBusy, setExtractBusy] = useState(false);
  const [extractMessage, setExtractMessage] = useState("");
  const [jobs, setJobs] = useState([]);
  const [cancellingJobId, setCancellingJobId] = useState(null);

  const loadStatus = useCallback(async () => {
    if (!userId) return;
    setStatusLoading(true);
    try {
      const data = await fetchJson(apiUrl(`/api/user/canvas-status?userId=${encodeURIComponent(userId)}`));
      setHasToken(!!data.hasToken);
      if (data.canvasBaseUrl) setCanvasBaseUrl(data.canvasBaseUrl.replace(/\/api\/v1\/?$/i, ""));
    } catch {
      setHasToken(false);
    } finally {
      setStatusLoading(false);
    }
  }, [userId]);

  const loadJobs = useCallback(async () => {
    if (!userId) return;
    try {
      const data = await fetchJson(apiUrl(`/api/extraction/jobs?userId=${encodeURIComponent(userId)}`));
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
    } catch {
      setJobs([]);
    }
  }, [userId]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    loadJobs();
    const id = setInterval(loadJobs, 2500);
    return () => clearInterval(id);
  }, [loadJobs]);

  const fetchCoursesList = useCallback(async () => {
    if (!userId || !hasToken) return;
    setCoursesLoading(true);
    try {
      const data = await fetchJson(apiUrl(`/api/canvas/enrolled-courses?userId=${encodeURIComponent(userId)}`));
      setCourses(Array.isArray(data.courses) ? data.courses : []);
    } catch {
      setCourses([]);
    } finally {
      setCoursesLoading(false);
    }
  }, [userId, hasToken]);

  useEffect(() => {
    fetchCoursesList();
  }, [fetchCoursesList]);

  const fetchModulesForCourse = useCallback(
    async (courseId) => {
      if (!userId || !courseId) {
        setModules([]);
        return;
      }
      setModulesLoading(true);
      try {
        const data = await fetchJson(
          apiUrl(
            `/api/canvas/course-modules?userId=${encodeURIComponent(userId)}&courseId=${encodeURIComponent(courseId)}`
          )
        );
        setModules(Array.isArray(data.modules) ? data.modules : []);
      } catch {
        setModules([]);
      } finally {
        setModulesLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    setSelectedModuleIds(new Set());
    if (selectedCourseId) fetchModulesForCourse(selectedCourseId);
    else setModules([]);
  }, [selectedCourseId, fetchModulesForCourse]);

  const publishedModules = useMemo(() => modules.filter((m) => m.published), [modules]);

  const toggleModule = (id) => {
    setSelectedModuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAllModules = () => {
    const ids = publishedModules.map((m) => m.id);
    setSelectedModuleIds(new Set(ids));
  };

  const clearModules = () => setSelectedModuleIds(new Set());

  const handleSaveCredentials = async (e) => {
    e.preventDefault();
    setSaveError("");
    if (!userId) return;
    setSaving(true);
    try {
      await apiPost("/api/user/canvas-credentials", {
        userId,
        canvasAccessToken: apiToken.trim(),
        canvasBaseUrl: canvasBaseUrl.trim(),
      });
      setApiToken("");
      setHasToken(true);
      await loadStatus();
      await fetchCoursesList();
    } catch (err) {
      setSaveError(err.message || "Could not save credentials.");
    } finally {
      setSaving(false);
    }
  };

  const handleExtract = async () => {
    setExtractMessage("");
    if (!userId || !selectedCourseId) {
      setExtractMessage("Select a course first.");
      return;
    }
    const moduleIds = Array.from(selectedModuleIds);
    setExtractBusy(true);
    try {
      const data = await apiPost("/api/extraction/start", {
        userId,
        courseId: selectedCourseId,
        moduleIds,
      });
      setExtractMessage(
        moduleIds.length
          ? `Extraction started for ${data.courseName || "course"} (${moduleIds.length} module${moduleIds.length === 1 ? "" : "s"}).`
          : `Extraction started for ${data.courseName || "course"} (all published modules).`
      );
      await loadJobs();
    } catch (err) {
      setExtractMessage(err.message || "Extraction could not be started.");
    } finally {
      setExtractBusy(false);
    }
  };

  const handleCancelJob = async (jobId) => {
    if (!userId || !jobId) return;
    setCancellingJobId(jobId);
    try {
      await apiPost("/api/extraction/cancel", { userId, jobId });
      await loadJobs();
    } catch (e) {
      setExtractMessage(e.message || "Could not cancel.");
    } finally {
      setCancellingJobId(null);
    }
  };

  const formatStatus = (s) => {
    if (s === "in_progress") return "In progress";
    if (s === "extracted") return "Extracted";
    if (s === "failed") return "Failed";
    if (s === "cancelled") return "Cancelled";
    return s || "—";
  };

  const badgeClass = (s) => {
    if (s === "extracted") return "bg-emerald-100 text-emerald-800";
    if (s === "failed") return "bg-red-100 text-red-800";
    if (s === "cancelled") return "bg-slate-200 text-slate-800";
    if (s === "in_progress") return "bg-amber-100 text-amber-800";
    return "bg-slate-100 text-slate-700";
  };

  if (!session) {
    return <Navigate to="/auth" replace />;
  }

  return (
    <div className="min-h-screen bg-secondary">
      <AppHeader titleSize="text-4xl" />
      <main className="px-6 py-12 pt-24 max-w-3xl mx-auto space-y-10">
        <div>
          <h1 className="text-3xl font-bold text-primary mb-2">Canvas onboarding</h1>
          <p className="text-slate-600">
            Connect your Canvas account so DeepToast can read module content for question generation and study workflows.
          </p>
        </div>

        <section className="bg-white rounded-2xl shadow-md p-6 space-y-4">
          <h2 className="text-xl font-semibold text-primary">1. Canvas API token</h2>
          <p className="text-sm text-slate-600">{TOKEN_SCOPE_NOTE}</p>

          <button
            type="button"
            className="text-sm font-semibold text-blue-700 underline"
            onClick={() => setShowHelp(true)}
          >
            How do I get an API token from Canvas?
          </button>

          {statusLoading ? (
            <p className="text-sm text-slate-500">Checking saved credentials…</p>
          ) : hasToken ? (
            <p className="text-sm font-semibold text-emerald-700">Your Canvas API token is saved. You can replace it below.</p>
          ) : null}

          <form onSubmit={handleSaveCredentials} className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Canvas site URL</label>
              <input
                type="url"
                value={canvasBaseUrl}
                onChange={(e) => setCanvasBaseUrl(e.target.value)}
                placeholder="https://your-school.instructure.com"
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                required
              />
              <p className="text-xs text-slate-500 mt-1">Use your institution&apos;s Canvas URL (same domain you use to log in).</p>
            </div>
            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">API access token</label>
              <input
                type="password"
                value={apiToken}
                onChange={(e) => setApiToken(e.target.value)}
                placeholder={hasToken ? "Enter a new token to replace the saved one" : "Paste access token"}
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                required={!hasToken}
              />
            </div>
            {saveError ? <p className="text-sm text-red-600">{saveError}</p> : null}
            <button
              type="submit"
              disabled={saving || (!hasToken && !apiToken.trim())}
              className="bg-primary text-white px-5 py-2 rounded-md font-semibold hover:opacity-95 disabled:opacity-60"
            >
              {saving ? "Saving…" : hasToken ? "Update token" : "Save token"}
            </button>
          </form>
        </section>

        {hasToken ? (
          <section className="bg-white rounded-2xl shadow-md p-6 space-y-4">
            <h2 className="text-xl font-semibold text-primary">2. Extract module content</h2>
            <p className="text-sm text-slate-600">
              Pick a course and the modules you want. Leave modules unselected to include every published module in that course.
            </p>

            <div>
              <label className="block text-sm font-semibold text-slate-700 mb-1">Course</label>
              <select
                value={selectedCourseId}
                onChange={(e) => setSelectedCourseId(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-primary bg-white"
                disabled={coursesLoading}
              >
                <option value="">{coursesLoading ? "Loading courses…" : "Select a course"}</option>
                {courses.map((c) => (
                  <option key={c.id} value={String(c.id)}>
                    {c.name}
                    {c.course_code ? ` (${c.course_code})` : ""}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <label className="block text-sm font-semibold text-slate-700">Modules (published)</label>
                <button
                  type="button"
                  className="text-xs font-semibold text-blue-700 underline"
                  onClick={selectAllModules}
                  disabled={!publishedModules.length || modulesLoading}
                >
                  Select all
                </button>
                <button
                  type="button"
                  className="text-xs font-semibold text-slate-600 underline"
                  onClick={clearModules}
                  disabled={!selectedModuleIds.size}
                >
                  Clear
                </button>
              </div>
              <div className="border border-slate-200 rounded-md max-h-56 overflow-y-auto divide-y divide-slate-100">
                {modulesLoading ? (
                  <p className="p-3 text-sm text-slate-500">Loading modules…</p>
                ) : !selectedCourseId ? (
                  <p className="p-3 text-sm text-slate-500">Choose a course to list modules.</p>
                ) : publishedModules.length === 0 ? (
                  <p className="p-3 text-sm text-slate-500">No published modules found.</p>
                ) : (
                  publishedModules.map((m) => (
                    <label key={m.id} className="flex items-center gap-3 px-3 py-2 hover:bg-slate-50 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedModuleIds.has(m.id)}
                        onChange={() => toggleModule(m.id)}
                      />
                      <span className="text-sm text-slate-800">{m.name}</span>
                    </label>
                  ))
                )}
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Selected: {selectedModuleIds.size || "all published (when none selected)"}
              </p>
            </div>

            {extractMessage ? <p className="text-sm text-slate-700">{extractMessage}</p> : null}

            <button
              type="button"
              onClick={handleExtract}
              disabled={extractBusy || !selectedCourseId}
              className="bg-primary text-white px-5 py-2 rounded-md font-semibold hover:opacity-95 disabled:opacity-60"
            >
              {extractBusy ? "Starting…" : "Extract module content"}
            </button>
          </section>
        ) : null}

        {hasToken ? (
          <section className="bg-white rounded-2xl shadow-md p-6 space-y-3">
            <h2 className="text-xl font-semibold text-primary">Extraction activity</h2>
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-slate-200">
                    <th className="py-2 pr-4 font-semibold text-slate-700">Course</th>
                    <th className="py-2 pr-4 font-semibold text-slate-700">Status</th>
                    <th className="py-2 pr-4 font-semibold text-slate-700 w-48">Progress</th>
                    <th className="py-2 pr-4 font-semibold text-slate-700">Started</th>
                    <th className="py-2 pr-4 font-semibold text-slate-700">Details</th>
                    <th className="py-2 font-semibold text-slate-700"> </th>
                  </tr>
                </thead>
                <tbody>
                  {jobs.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="py-4 text-slate-500">
                        No extractions yet.
                      </td>
                    </tr>
                  ) : (
                    jobs.map((j) => (
                      <tr key={j.jobId || j._id} className="border-b border-slate-100 align-top">
                        <td className="py-2 pr-4">{j.courseName || j.courseId}</td>
                        <td className="py-2 pr-4">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${badgeClass(j.status)}`}>
                            {formatStatus(j.status)}
                          </span>
                        </td>
                        <td className="py-2 pr-4">
                          {j.status === "in_progress" ? (
                            <div className="space-y-1">
                              <div className="h-2 rounded-full bg-slate-200 overflow-hidden w-full max-w-[10rem]">
                                <div
                                  className="h-full bg-primary transition-all duration-300"
                                  style={{ width: `${Math.min(100, Math.max(0, Number(j.progressPercent) || 0))}%` }}
                                />
                              </div>
                              <p className="text-xs text-slate-600">{Math.round(Number(j.progressPercent) || 0)}%</p>
                              {j.progressLabel ? (
                                <p className="text-xs text-slate-500 truncate max-w-[14rem]" title={j.progressLabel}>
                                  {j.progressLabel}
                                </p>
                              ) : j.totalUnits ? (
                                <p className="text-xs text-slate-500">
                                  {Number(j.processedUnits) || 0} / {Number(j.totalUnits) || 0} items
                                </p>
                              ) : null}
                            </div>
                          ) : j.status === "extracted" ? (
                            <span className="text-xs text-emerald-700 font-medium">100%</span>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                        <td className="py-2 pr-4 text-slate-600 whitespace-nowrap">
                          {j.createdAt ? new Date(j.createdAt).toLocaleString() : "—"}
                        </td>
                        <td className="py-2 text-slate-600 pr-4">
                          {j.status === "failed" && j.errorMessage ? (
                            <span className="text-red-700">{String(j.errorMessage).slice(0, 160)}</span>
                          ) : j.status === "cancelled" && j.errorMessage ? (
                            <span className="text-slate-600">{String(j.errorMessage).slice(0, 160)}</span>
                          ) : j.finishedAt ? (
                            <span className="text-slate-500">Finished {new Date(j.finishedAt).toLocaleString()}</span>
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="py-2">
                          {j.status === "in_progress" && j.jobId ? (
                            <button
                              type="button"
                              onClick={() => handleCancelJob(j.jobId)}
                              disabled={cancellingJobId === j.jobId}
                              className="text-sm font-semibold text-red-700 underline disabled:opacity-50"
                            >
                              {cancellingJobId === j.jobId ? "Cancelling…" : "Cancel"}
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </section>
        ) : null}

        <div className="flex flex-wrap gap-3 justify-between items-center pb-12">
          <button
            type="button"
            onClick={() => navigate("/deeptoast")}
            className="text-primary font-semibold underline disabled:opacity-40"
            disabled={!hasToken}
          >
            Continue to DeepToast
          </button>
          <button
            type="button"
            onClick={() => navigate("/")}
            className="text-slate-600 font-semibold underline"
          >
            Back to home
          </button>
        </div>
      </main>

      <Modal open={showHelp} title="How to create a Canvas API token" onClose={() => setShowHelp(false)}>
        <ol className="list-decimal pl-5 space-y-2">
          <li>Sign in to your school&apos;s Canvas site (the same URL you use for classes).</li>
          <li>Open <strong>Account</strong> → <strong>Settings</strong>.</li>
          <li>Scroll to <strong>Approved Integrations</strong> and choose <strong>New Access Token</strong>.</li>
          <li>Give the token a purpose (for example, &quot;DeepToast&quot;) and create it.</li>
          <li>Copy the token immediately — Canvas shows it only once — and paste it into DeepToast.</li>
        </ol>
        <p className="text-xs text-slate-500 mt-3">
          If your school uses a custom Canvas domain, use that exact domain in the Canvas site URL field above.
        </p>
      </Modal>
    </div>
  );
}
